<div class="d-flex analysis-layout">
  <!-- Left Sidebar Navigation -->
  <app-analysis-nav [analysisType]="analysisType" [config]="navConfig"> </app-analysis-nav>

  <!-- Main Content -->
  <div class="content-area">
    <app-loader [isLoading]="isLoading"></app-loader>

    <h2>Allele Usage in repertoires</h2>

    @if (projectViewData) {
      <div>
        <div class="row p-3 justify-content-md-center">
          <div class="row p-3">
            <div class="col-2">
              <label>Locus</label>
              <select class="form-select" [value]="selectedLocus" (change)="onSelectedLocus($event)">
                @for (locus of projectViewData.loci_covered; track locus) {
                  <option [value]="locus">
                    {{ locus }}
                  </option>
                }
              </select>
            </div>
          </div>
        </div>
      </div>
    }

    <span id="alleleExpressionSpan" [attr.style]="alleleExpressionStyle">
      <div class="row p-3 justify-content-md-center">
        <h3>Allele Expression</h3>
        <div class="table-controls">
          <div class="table-actions" role="group" aria-label="Export allele usage statistics">
            <button
              class="btn btn-outline-secondary"
              type="button"
              [disabled]="!usageRows.length"
              (click)="copyUsageTable()"
            >
              Copy
            </button>
            <button
              class="btn btn-outline-secondary"
              type="button"
              [disabled]="!usageRows.length"
              (click)="exportUsageExcel()"
            >
              Excel
            </button>
            <button
              class="btn btn-outline-secondary"
              type="button"
              [disabled]="!usageRows.length"
              (click)="exportUsagePdf()"
            >
              PDF
            </button>
          </div>
          @if (exportMessage) {
            <p class="export-message" role="status">{{ exportMessage }}</p>
          }
          <div class="global-filter">
            <label class="visually-hidden" for="allele-usage-global-filter">Search allele usage statistics</label>
            <input
              id="allele-usage-global-filter"
              class="form-control"
              type="search"
              placeholder="Search all columns"
              [value]="globalFilter"
              (input)="onGlobalFilterChange($event, usageTable)"
            />
          </div>
        </div>
        <p-table
          #usageTable
          [value]="usageRows"
          [columns]="usageColumns"
          [filters]="usageFilters"
          [globalFilterFields]="globalFilterFields"
          (onFilter)="alleleUsageUpdateHandler($event)"
          [paginator]="true"
          [rows]="10"
          [rowsPerPageOptions]="pageSizes"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          class="allele-usage-table"
        >
          <ng-template pTemplate="header" let-columns>
            <tr>
              @for (column of columns; track column.data) {
                <th [pSortableColumn]="column.data">
                  <div class="table-header-content">
                    <span class="table-header-title">
                      {{ column.title }}
                      <p-sortIcon [field]="column.data"></p-sortIcon>
                    </span>
                    <p-columnFilter
                      [field]="column.data"
                      [type]="isNumericColumn(column) ? 'numeric' : 'text'"
                      display="menu"
                      [showOperator]="true"
                      [showMatchModes]="true"
                      [showAddButton]="true"
                      [filterButtonProps]="filterButtonProps"
                    />
                  </div>
                </th>
              }
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-rowData let-columns="columns">
            <tr>
              @for (column of columns; track column.data) {
                <td>{{ rowData[column.data] }}</td>
              }
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage" let-columns>
            <tr>
              <td [attr.colspan]="columns.length || 1">No allele usage data available.</td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          role="switch"
          id="flexSwitchCheckDefault"
          [disabled]="!searchActive"
          (change)="onCompareSelectedRows($event)"
        />
        <label class="form-check-label" for="flexSwitchCheckDefault">Compare selected and unselected rows</label>
      </div>

      <div class="mb-3">
        <label class="form-label">Chart Type</label>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="chartType"
            id="barChartRadio"
            value="bar"
            [checked]="!showViolinPlots"
            (change)="onChartTypeChange($event)"
          />
          <label class="form-check-label" for="barChartRadio">Bar Chart</label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="chartType"
            id="violinPlotRadio"
            value="violin"
            [checked]="showViolinPlots"
            (change)="onChartTypeChange($event)"
          />
          <label class="form-check-label" for="violinPlotRadio">Violin Plot</label>
        </div>
      </div>

      @if (showViolinPlots) {
        <div>
          @for (gtype of getalleleUsageGeneTypes(); track gtype) {
            <div>
              <div class="row p-12 justify-content-md-center">
                {{ gtype }}
                @if (alleleUsagePlotData) {
                  <div class="col-md-auto" [attr.style]="getalleleUsageCanvasStyle(gtype)">
                    <canvas
                      baseChart
                      [data]="alleleUsagePlotData[gtype]"
                      [options]="violinChartAlleleUsageOptions"
                      [legend]="false"
                      [type]="'violin'"
                    >
                    </canvas>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
      @if (!showViolinPlots) {
        <div>
          @for (gtype of getalleleUsageGeneTypes(); track gtype) {
            <div>
              <div class="row p-12 justify-content-md-center">
                {{ gtype }}
                @if (alleleUsagePlotData) {
                  <div class="col-md-auto" [attr.style]="getalleleUsageCanvasStyle(gtype)">
                    <canvas
                      baseChart
                      [data]="alleleUsagePlotData[gtype]"
                      [options]="barChartAlleleUsageOptions"
                      [legend]="false"
                      [type]="'bar'"
                    >
                    </canvas>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </span>
  </div>
</div>
